import { useCallback } from "react";
import {useBearerToken, useCdsid} from "../context/AuthContext";
import { jwtDecode } from "jwt-decode";

interface DecodedToken {
  sub?: string;
  exp?: number;
  [key: string]: any;
}

export const useGet = () => {
  const bearerToken = useBearerToken();
  const cdsid = useCdsid();

  /**
   * CWE-639 FIX: Validate token claims before making API request
   */
  const validateTokenBeforeRequest = (token: string, url: string): boolean => {
    try {
      const decoded = jwtDecode<DecodedToken>(token);
      
      // Check 1: Token not expired
      if (decoded.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (now >= decoded.exp) {
          console.error('SECURITY: Token has expired');
          return false;
        }
      }

      // Check 2: For /mdp endpoints, backend will validate token and extract cdsId from token.sub
      // No parameter validation needed since cdsId is not in URL anymore

      return true;
    } catch (error) {
      console.error('SECURITY: Token validation failed:', error);
      return false;
    }
  };

  /**
   * CWE-639 FIX: Validate response integrity before returning it
   */
  const validateResponseIntegrity = (response: any, url: string): boolean => {
    if (url.includes('/mdp')) {
      if (!response || !response.Permitted || !Array.isArray(response.Permitted)) {
        console.error('SECURITY: Invalid /mdp response structure detected');
        return false;
      }

      if (response.user && response.user !== cdsid) {
        console.error(
          `SECURITY ALERT: Response user (${response.user}) does not match current user (${cdsid}). ` +
          `This response may have been tampered with.`
        );
        return false;
      }
    }

    return true;
  };

  const getData = useCallback(
    async (url: string): Promise<any> => {

      if (!bearerToken) {
        console.error(`Aborting GET request to ${url}: No bearer token found.`);
        return null;
      }

      // CWE-639 FIX: Validate token before making request
      if (!validateTokenBeforeRequest(bearerToken, url)) {
        console.error(`SECURITY: Token validation failed for URL: ${url}. Request aborted.`);
        return null;
      }

      try {
        const headers = new Headers();
        if (bearerToken) {
          headers.append("Authorization", `Bearer ${bearerToken}`);
        }
        headers.append("Content-Type", "application/json");
        headers.append("Accept", "application/json");

        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP ERROR STATUS: ${response.status} - ${errorText} - URL: ${url}`
          );
        }

        const json = await response.json();

        // CWE-639 FIX: Validate response before returning it
        if (!validateResponseIntegrity(json, url)) {
          console.error(`SECURITY: Response validation failed for ${url}. Rejecting response.`);
          return null;
        }

        return json;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [bearerToken, cdsid]
  );

  return { getData };
};
